import PropTypes from 'prop-types';
import React, { useCallback, useContext, useMemo } from 'react';
import { withServerContext } from '@bingads-webui-campaign-react/server-context';
import { GridContext } from '@bingads-webui-grid/grid-context';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { useWizardService, WIZARD_EVENTS } from '@bingads-webui-react/wizard';
import {
  createTableColumn,
  DataGrid,
  DataGridBody,
  DataGridCell,
  DataGridHeader,
  DataGridHeaderCell,
  DataGridRow,
  Spinner,
  TableCellLayout,
} from '@fluentui/react-components';
import { Pause12Filled, PresenceBusy12Filled } from '@fluentui/react-icons';
import { budgetFormatOptions } from '../../constants/budget-format-options';
import {
  CAMPAIGN_PAUSED_STATUSES,
  CAMPAIGN_TYPE_DISPLAY,
  CAMPAIGN_TYPE_FEED_BASED,
  CAMPAIGN_TYPE_NATIVE,
  CAMPAIGN_TYPE_ONLINE_VIDEO,
} from '../../constants/campaign';
import { EDIT_INTENT } from '../../constants/intents';
import { useSelectCampaignGridStyles } from '../../styles';

const getCampaignSubtypeText = (subType) => {
  switch (subType) {
    case 'AudienceStatic':
      return CAMPAIGN_TYPE_NATIVE;
    case 'BrandAwarenessVideoAds':
    case 'AudienceVideoAds':
      return CAMPAIGN_TYPE_ONLINE_VIDEO;
    case 'AudienceFeedBased':
    case 'AudienceDynamicFeedBased':
      return CAMPAIGN_TYPE_FEED_BASED;
    case 'DisplayAds':
    default:
      return CAMPAIGN_TYPE_DISPLAY;
  }
};

const getAudienceTargetingText = (targetingEnabled) => {
  if (targetingEnabled === null) {
    return _TL_('');
  }

  return targetingEnabled ? _TL_('Enabled') : _TL_('Disabled');
};

const getColumns = ({ CurrencyCode, i18n }) => [
  createTableColumn({
    columnId: 'Name',
    compare: (a, b) => a.Name.localeCompare(b.Name),
    renderCell: ((campaign) => {
      let nameIcon = <PresenceBusy12Filled primaryFill="#0F7C0F" />;
      if (CAMPAIGN_PAUSED_STATUSES.includes(campaign.Status)) {
        nameIcon = <Pause12Filled primaryFill="#262626" />;
      } else if (!campaign.IsAudienceTargetingEnabled) {
        nameIcon = <PresenceBusy12Filled primaryFill="#262626" />;
      }
      return (
        <TableCellLayout media={nameIcon} truncate>
          {campaign.Name}
        </TableCellLayout>
      );
    }),
    renderHeaderCell: () => i18n.getString(_TL_('Campaign name')),
  }),
  createTableColumn({
    columnId: 'CampaignSubType',
    renderCell: campaign => i18n.getString(getCampaignSubtypeText(campaign.CampaignSubType)),
    renderHeaderCell: () => i18n.getString(_TL_('Campaign type')),
  }),
  createTableColumn({
    columnId: 'IsAudienceTargetingEnabled',
    renderCell: campaign => i18n.getString(getAudienceTargetingText(campaign.IsAudienceTargetingEnabled)),
    renderHeaderCell: () => i18n.getString(_TL_('Audience targeting')),
  }),
  createTableColumn({
    columnId: 'DailyTargetBudgetAmount',
    compare: (a, b) => a.DailyTargetBudgetAmount - b.DailyTargetBudgetAmount,
    renderCell: campaign => i18n.formatCurrency(campaign.DailyTargetBudgetAmount, CurrencyCode, budgetFormatOptions),
    renderHeaderCell: () => i18n.getString(_TL_('Budget per day')),
  }),
  createTableColumn({
    columnId: 'IsEligibleForMSANLiftExperiment',
    compare: (a, b) => a.IsEligibleForMSANLiftExperiment - b.IsEligibleForMSANLiftExperiment,
    renderCell: campaign => i18n.getString(campaign.IsEligibleForMSANLiftExperiment ? _TL_('Eligible') : _TL_('Ineligible')),
    renderHeaderCell: () => i18n.getString(_TL_('Campaign eligibility')),
  }),
];

const SelectCampaignsGridInternal = ({
  onCampaignsChange,
  serverContext,
}) => {
  const styles = useSelectCampaignGridStyles();

  const { CurrentAccount } = serverContext;
  const { CurrencyCode } = CurrentAccount;

  const { dispatch, state } = useContext(GridContext);
  const { state: wizardState, send } = useWizardService();
  const i18n = useContext(I18nContext);
  const columns = getColumns({ CurrencyCode, i18n });

  const onChangeSelectedCampaigns = useCallback((_event, { selectedItems }) => {
    const { selectedCampaignsById } = wizardState.context.wizardData;

    const eligibleCampaigns = state.items ? state.items.filter(campaign => campaign.IsEligibleForMSANLiftExperiment) : [];
    // Datagrid nas no notion of disabled items, so once something on page is selected - it always gives you back the entire page when clicking select all
    const isSelectAll = selectedItems.size >= state.pagination.pageSize;
    if (isSelectAll) {
      const isAllEligibleCampaignsSelected = eligibleCampaigns.every(campaign => selectedCampaignsById[campaign.Id]);
      if (isAllEligibleCampaignsSelected) {
        eligibleCampaigns.forEach((campaign) => {
          delete selectedCampaignsById[campaign.Id];
        });
      } else {
        eligibleCampaigns.forEach((campaign) => {
          selectedCampaignsById[campaign.Id] = campaign;
        });
      }
    } else {
      eligibleCampaigns.forEach((campaign) => {
        if (selectedItems.has(campaign.Id)) {
          selectedCampaignsById[campaign.Id] = campaign;
        } else if (selectedCampaignsById[campaign.Id]) {
          delete selectedCampaignsById[campaign.Id];
        }
      });
    }
    send(WIZARD_EVENTS.UPDATE_WIZARD_DATA, { selectedCampaignsById });
    onCampaignsChange({ count: Object.keys(selectedCampaignsById).length });
  }, [onCampaignsChange, send, state.items, state.pagination.pageSize, wizardState.context.wizardData]);

  const selectedItems = useMemo(() => {
    const selectedCampaignsIds = Object.keys(wizardState.context.wizardData.selectedCampaignsById).map(idAsString => Number(idAsString));
    return new Set(selectedCampaignsIds);
    // eslint-disable-next-line
  }, [wizardState.context.wizardData.selectedCampaignsById, Object.keys(wizardState.context.wizardData.selectedCampaignsById).length]);

  const onSortChange = useCallback((e, { sortColumn, sortDirection }) => {
    dispatch({
      type: 'UPDATE_SORTED_COLUMN',
      payload: {
        ...state.sortedColumn,
        fieldName: sortColumn,
        isSortDescending: sortDirection === 'descending',
      },
    });
  }, [dispatch, state]);

  const { isInitialized, intent } = wizardState.context.wizardData;
  const isEdit = intent === EDIT_INTENT;
  return (
    <DataGrid
      columns={columns}
      getRowId={campaign => campaign.Id}
      items={state.items || []}
      onSelectionChange={onChangeSelectedCampaigns}
      onSortChange={onSortChange}
      selectionMode="multiselect"
      sortable
      selectedItems={selectedItems}
    >
      <DataGridHeader>
        <DataGridRow
          select
          selectionCell={{
            checkboxIndicator: { 'aria-label': i18n.getString(_TL_('Select all campaigns')) },
          }}
        >
          {({ renderHeaderCell }) => (
            <DataGridHeaderCell>{renderHeaderCell()}</DataGridHeaderCell>
          )}
        </DataGridRow>
      </DataGridHeader>
      {((isEdit && !isInitialized) || state.items === null) ? (
        <Spinner className={styles.spinner} label={i18n.getString(_TL_('Loading campaigns...'))} />
      ) : (
        <DataGridBody>
          {({ item, rowId }) => (
            <DataGridRow
              className={item.IsEligibleForMSANLiftExperiment ? styles.dataGridRow : styles.dataGridRowDisabled}
              key={rowId}
              selectionCell={{
                checkboxIndicator: { 'aria-label': i18n.getString(_TL_('Select campaign')) },
              }}
            >
              {({ renderCell }) => (
                <DataGridCell className={styles.selectCampaignTableCell}>
                  {renderCell(item)}
                </DataGridCell>
              )}
            </DataGridRow>
          )}
        </DataGridBody>
      )}
    </DataGrid>
  );
};

SelectCampaignsGridInternal.propTypes = {
  onCampaignsChange: PropTypes.func.isRequired,
  serverContext: PropTypes.shape({
    CurrentAccount: PropTypes.shape({
      CurrencyCode: PropTypes.string.isRequired,
    }).isRequired,
  }).isRequired,
};

export const SelectCampaignsGrid = withServerContext(SelectCampaignsGridInternal);
